/**
 * Throws a tile into the settings gear in the sidebar. This is the whole explanation the user gets
 * for where the knowledge base went, so it is deliberately slow enough to follow — and skipped
 * outright when the machine asks for less motion, where a teleport reads as intended.
 *
 * The tile is drawn here rather than cloned from the rail: a clone leaves the component's own
 * styles behind (they are written against `:host`) and arrives as unstyled blue text.
 */
const DURATION_MS = 1150;
/** How far the arc bows out sideways from the straight line. */
const ARC_BOW_PX = 160;
const TILE_PX = 56;

export async function flyToSettings(source: HTMLElement): Promise<void> {
  const target = document.querySelector<HTMLElement>('[data-settings-anchor]');
  if (!target) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const from = source.getBoundingClientRect();
  const to = target.getBoundingClientRect();
  const startX = from.left + from.width / 2 - TILE_PX / 2;
  const startY = from.top + from.height / 2 - TILE_PX / 2;
  const dx = to.left + to.width / 2 - (from.left + from.width / 2);
  const dy = to.top + to.height / 2 - (from.top + from.height / 2);

  // Bow perpendicular to the flight, not upwards: the gear usually sits straight below the item in
  // the rail, and lifting a vertical flight only makes it longer. Perpendicular (dy, -dx) swings a
  // downward flight out to the right, which is the way into the page rather than off-screen.
  const length = Math.hypot(dx, dy) || 1;
  const bowX = (dy / length) * ARC_BOW_PX;
  const bowY = (-dx / length) * ARC_BOW_PX;

  const icon = source.querySelector('.material-icons')?.textContent?.trim() || 'auto_stories';
  const tile = document.createElement('div');
  tile.textContent = icon;
  tile.className = 'material-icons';
  tile.style.cssText = [
    'position:fixed',
    `left:${startX}px`,
    `top:${startY}px`,
    `width:${TILE_PX}px`,
    `height:${TILE_PX}px`,
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'font-size:26px',
    'color:#fff',
    'border-radius:16px',
    'background:linear-gradient(135deg, var(--p-primary-500), var(--p-primary-600))',
    'box-shadow:0 12px 32px color-mix(in srgb, var(--p-primary-500) 45%, transparent)',
    'pointer-events:none',
    'z-index:2000',
  ].join(';');
  document.body.appendChild(tile);

  // A three-keyframe path interpolates in straight lines, which puts a corner at the apex. Sampling
  // a quadratic Bézier instead gives the curve itself; the control point sits twice the bow out, so
  // the curve — which only reaches halfway to it — peaks at the bow.
  const controlX = dx / 2 + bowX * 2;
  const controlY = dy / 2 + bowY * 2;
  const STEPS = 16;
  const frames = Array.from({ length: STEPS + 1 }, (_, step) => {
    const t = step / STEPS;
    const x = 2 * (1 - t) * t * controlX + t * t * dx;
    const y = 2 * (1 - t) * t * controlY + t * t * dy;
    // Shrinks over the whole flight, but only disappears at the gear: fading early reads as the
    // tile giving up rather than arriving.
    const scale = 1 - 0.85 * t * t;
    const opacity = t < 0.82 ? 1 : 1 - (t - 0.82) / 0.18;
    return { transform: `translate(${x}px, ${y}px) scale(${scale})`, opacity, offset: t };
  });

  const flight = tile.animate(frames, { duration: DURATION_MS, easing: 'cubic-bezier(0.36, 0, 0.28, 1)' });

  target.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.4)' }, { transform: 'scale(1)' }], {
    duration: 460,
    delay: DURATION_MS - 300,
    easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  });

  try {
    await flight.finished;
  } finally {
    tile.remove();
  }
}
